import {
  type ConfirmarResetDeSenha,
  type Desafio2fa,
  type Login,
  type ProvedorOAuth,
  type RespostaDeLogin,
  type Sessao,
  type SetupTotpResposta,
  provedoresDisponiveisSchema,
  respostaDeLoginSchema,
  sessaoSchema,
  setupTotpRespostaSchema,
} from '@locaveri/shared'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet, apiPost } from '@/lib/api'
import { ROTAS_DE_AUTENTICACAO, rotaDeAutorizacaoOAuth } from '@/lib/endpoints'
import { env } from '@/lib/env'

/**
 * Chamadas de autenticação.
 *
 * Todas passam `semAutenticacao: true`: são as rotas em que ainda não existe (ou
 * não vale) um access token, e um 401 delas significa "credencial errada", não
 * "sessão expirada". Se o interceptor tentasse renovar aqui, uma senha errada
 * viraria uma tentativa de refresh — ruído no log de segurança e um caminho a
 * mais para o lockout disparar sem motivo.
 *
 * Exceção: o setup do 2FA acontece COM sessão (o usuário já entrou, mas ainda
 * não pode navegar) e por isso vai autenticado.
 */

/** Resposta de endpoint que só confirma ("enviamos o e-mail"). */
const respostaSimplesSchema = z.unknown()

export function entrar(dados: Login): Promise<RespostaDeLogin> {
  return apiPost(ROTAS_DE_AUTENTICACAO.login, respostaDeLoginSchema, dados, {
    semAutenticacao: true,
  })
}

/** `true` quando a API pediu o segundo fator em vez de devolver a sessão. */
export function ehDesafioDe2fa(
  resposta: RespostaDeLogin,
): resposta is Extract<RespostaDeLogin, { status: '2FA_OBRIGATORIO' }> {
  return 'status' in resposta && resposta.status === '2FA_OBRIGATORIO'
}

/** `true` no primeiro login de papel administrativo: falta CONFIGURAR o 2FA. */
export function ehSetupDe2faObrigatorio(
  resposta: RespostaDeLogin,
): resposta is Extract<RespostaDeLogin, { status: '2FA_SETUP_OBRIGATORIO' }> {
  return 'status' in resposta && resposta.status === '2FA_SETUP_OBRIGATORIO'
}

export function responderDesafio2fa(dados: Desafio2fa): Promise<Sessao> {
  return apiPost(ROTAS_DE_AUTENTICACAO.desafio2fa, sessaoSchema, dados, { semAutenticacao: true })
}

export function solicitarResetDeSenha(email: string): Promise<unknown> {
  return apiPost(
    ROTAS_DE_AUTENTICACAO.esqueciSenha,
    respostaSimplesSchema,
    { email },
    { semAutenticacao: true },
  )
}

export function redefinirSenha(dados: ConfirmarResetDeSenha): Promise<unknown> {
  return apiPost(ROTAS_DE_AUTENTICACAO.redefinirSenha, respostaSimplesSchema, dados, {
    semAutenticacao: true,
  })
}

/** Inicia a configuração do TOTP. Exige sessão. */
/**
 * Com `desafioId` (o setup OBRIGATÓRIO do primeiro login), a chamada vai SEM
 * Bearer — ainda não existe sessão; o desafio é a credencial.
 */
export function iniciarSetupTotp(desafioId?: string): Promise<SetupTotpResposta> {
  return apiPost(
    ROTAS_DE_AUTENTICACAO.setupTotp,
    setupTotpRespostaSchema,
    desafioId === undefined ? {} : { desafioId },
    desafioId === undefined ? {} : { semAutenticacao: true },
  )
}

/**
 * Confirma o TOTP com o primeiro código. A API reemite a sessão porque o
 * access token passa a carregar `totp: true` — sem isso o usuário continuaria
 * com um token que diz "não passou pelo segundo fator".
 */
export function confirmarTotp(codigoTotp: string, desafioId?: string): Promise<Sessao> {
  return apiPost(
    ROTAS_DE_AUTENTICACAO.confirmarTotp,
    sessaoSchema,
    desafioId === undefined ? { codigoTotp } : { codigoTotp, desafioId },
    desafioId === undefined ? {} : { semAutenticacao: true },
  )
}

const CHAVE_DE_PROVEDORES = ['auth', 'provedores'] as const

/**
 * Provedores de login social habilitados NESTE ambiente.
 *
 * As credenciais de Google e Apple ainda não existem (ver ROADMAP). Quem decide
 * é a API, lendo o próprio `.env`: sem a variável, o provedor não aparece nesta
 * lista e o botão simplesmente não é renderizado. Se a consulta falhar, o
 * resultado é o mesmo — nenhum botão social. Ausência de credencial desabilita
 * o botão; não derruba a tela de login.
 */
export function useProvedoresOAuth(): UseQueryResult<readonly ProvedorOAuth[]> {
  return useQuery({
    queryKey: CHAVE_DE_PROVEDORES,
    queryFn: async ({ signal }) => {
      const resposta = await apiGet(ROTAS_DE_AUTENTICACAO.provedores, provedoresDisponiveisSchema, {
        semAutenticacao: true,
        signal,
      })

      return resposta.provedores
    },
    // A lista muda quando o ambiente muda, ou seja, quase nunca.
    staleTime: 10 * 60 * 1000,
    retry: false,
  })
}

/**
 * Manda o navegador para o provedor.
 *
 * É NAVEGAÇÃO, não `fetch`: o fluxo Authorization Code + PKCE exige que o
 * `state` e o `code_verifier` fiquem do lado do servidor, e um redirecionamento
 * de verdade é o que permite ao provedor mostrar a própria tela de consentimento
 * (além de ser o único jeito de o Apple Sign In funcionar). A API responde 302,
 * trata o retorno, grava o cookie httpOnly de refresh e devolve o navegador
 * para `/login/retorno`.
 */
export function irParaLoginSocial(provedor: ProvedorOAuth): void {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, '')
  const retorno = `${window.location.origin}/login/retorno`
  const url = `${base}${rotaDeAutorizacaoOAuth(provedor)}?retorno=${encodeURIComponent(retorno)}`

  window.location.assign(url)
}
